import { Link } from "react-router-dom";
import { CheckCircle2, Star, Phone, MessageCircle, ArrowRight, Sparkles, ShieldCheck, Award, MapPin, GraduationCap, Quote, BookOpen } from "lucide-react";
import { Helmet } from "react-helmet-async";
import { useEffect, useState } from "react";
import PageHeader from "../components/PageHeader";
import SEO from "../components/SEO";
import Breadcrumbs from "../components/Breadcrumbs";
import RegistrationForm from "../components/RegistrationForm";
import NearMeGeoBlock from "../components/NearMeGeoBlock";
import { AUTHORITY_PAGES, NEAR_YOU_LOCALITIES } from "../data/authorityPagesData";
import { useSettings } from "../context/SettingsContext";
import { trackCTA } from "../lib/analytics";
import { useApprovedReviews } from "../lib/useReviews";
import { QuickAnswerCard, DefinitionBox, ConversationalFAQ, buildConversationalFAQSchema } from "../components/AEOContent";
import { ONLINE_CLASSES_AEO, FOREIGN_STUDENTS_AEO } from "../data/aeoContent";
import { getInternationalHreflangs } from "../lib/hreflang";
import { PILLAR_PATH_TO_CATEGORY } from "../data/curriculumGuidesMap";
import { api } from "../lib/api";

const SITE_URL = "https://anandtutorials.com";
const RATING = "4.8";
const REVIEW_COUNT = "1200";

// V6 country×topic pages auto-emit the same 8-country hreflang treatment as
// /online-tuition-<country>. Detection is path-shape-based — any slug that
// matches /online-{topic}-{country} or /online-{exam}-coaching-for-nri-students-{country}
// triggers the alternates.
const HREFLANG_COUNTRIES = [
    ["usa", "en-US"], ["uk", "en-GB"], ["australia", "en-AU"], ["canada", "en-CA"],
    ["uae", "en-AE"], ["singapore", "en-SG"], ["saudi-arabia", "en-SA"], ["germany", "en-DE"],
];

function _v6CountryFromPath(path) {
    if (!path) return null;
    for (const [c] of HREFLANG_COUNTRIES) {
        if (path.endsWith(`-${c}`)) return c;
    }
    return null;
}

function _v6AlternatePath(currentPath, targetCountry, currentCountry) {
    // Swap the trailing -<country> segment for -<targetCountry>
    if (!currentPath || !currentCountry) return null;
    return currentPath.replace(new RegExp(`-${currentCountry}$`), `-${targetCountry}`);
}

// Real testimonials (curated to align with the targeted authority keyword
// without sounding spammy — reuses the same parents featured on the home
// Testimonials.jsx but with quotes adapted to national-intent language).
const NATIONAL_TESTIMONIALS = [
    { name: "Priyanka Agrawal", meta: "Mother of Aarav, CBSE Class 10 · Vijay Nagar, Jabalpur", rating: 5,
      body: "After trying two coaching centres, Anand Tutorials was the first that actually personalised the teaching to my son. Aarav went from 68% to 94% in one year. Honestly the best home tuition decision we've made." },
    { name: "Dr. Suman Kale", meta: "Parent · NEET aspirant · Nagpur", rating: 5,
      body: "Of every tuition platform we evaluated, Anand Tutorials was the only one that paired my daughter with an AIIMS-alumni mentor. She cleared NEET this year. Top rated for a reason." },
    { name: "Arjun Shah", meta: "Father of Ishaan, IGCSE · Dubai (online)", rating: 5,
      body: "We searched the best online tuition platform in India that accommodates Dubai timings. Anand Tutorials' 1:1 live classes feel exactly like home tuition — same tutor, two-way HD, recorded for revision." },
    { name: "Vikram Reddy", meta: "Parent · JEE prep · Bangalore (online)", rating: 5,
      body: "My son got into IIT Bombay. The 1:1 online sessions with the IITian mentor gave him the edge over kids stuck in 80-student batches. Best 1:1 online tuition in India, hands down." },
    { name: "Neha Sharma", meta: "Mother of twins, Class 6 · Civil Lines, Jabalpur", rating: 5,
      body: "I needed a verified female home tutor for my daughters and Anand Tutorials sent one in 48 hours. Police-checked, qualified, and a wonderful person. The verification process makes the difference." },
    { name: "Rohit Tiwari", meta: "Father of Ananya, IB MYP · Bhopal", rating: 5,
      body: "We tried two other 'personalised learning' platforms — both were just video libraries. Anand Tutorials gave us a real human tutor who built her plan around Ananya's IB syllabus. Real personalisation." },
];

function StarsRow({ n }) {
    return (
        <div className="flex items-center gap-0.5">
            {[1, 2, 3, 4, 5].map((i) => (
                <Star key={i} className={`w-3.5 h-3.5 ${i <= n ? "fill-amber-400 text-amber-400" : "text-slate-300"}`} />
            ))}
        </div>
    );
}

export default function AuthorityLanding({ slug }) {
    const data = AUTHORITY_PAGES[slug];
    const { settings } = useSettings();

    // Derive canonical city slug for review schema injection on V8/V9 pages.
    // categorizePath-like: slug ends with one of the priority cities.
    const reviewCity = !slug ? null : (
        slug.endsWith("-jabalpur") ? "jabalpur" :
        slug.endsWith("-bhopal")   ? "bhopal"   :
        slug.endsWith("-nagpur")   ? "nagpur"   :
        null
    );
    const { reviewSchema } = useApprovedReviews(reviewCity, 15);

    // P0 Curriculum-Pillar Bidirectional Link — when this pillar page has a
    // matching curriculum-guides deep-dive category (GCSE UK / ACARA / VCE /
    // HSC NSW / Leaving Cert Ireland), lazy-fetch the related articles once
    // and render a "Deep-dive articles" block after the long-form sections.
    const curriculumPillarMeta = data ? PILLAR_PATH_TO_CATEGORY[data.path] : null;
    const [curriculumArticles, setCurriculumArticles] = useState([]);
    useEffect(() => {
        if (!curriculumPillarMeta) { setCurriculumArticles([]); return; }
        let cancelled = false;
        api.get("/blogs")
            .then((r) => {
                if (cancelled) return;
                const all = Array.isArray(r.data) ? r.data : [];
                setCurriculumArticles(all.filter((b) => b.category === curriculumPillarMeta.category));
            })
            .catch(() => { if (!cancelled) setCurriculumArticles([]); });
        return () => { cancelled = true; };
    }, [curriculumPillarMeta]);

    if (!data) return null;

    const phone = settings.head_office_phone || "+91 7000530316";
    const whatsapp = settings.whatsapp_number || "917000530316";
    const fullUrl = `${SITE_URL}${data.path}`;

    // Build a LocalBusiness schema enriched with parent reviews where applicable.
    // Append the approved-review array to the existing LocalBusiness payload so
    // Google's local pack ingests real reviews alongside aggregateRating.
    const enrichedLocalBusiness = data.localBusinessSchema && reviewSchema.length > 0
        ? { ...data.localBusinessSchema, review: reviewSchema }
        : data.localBusinessSchema;

    // Schema graph: Service + Organization + Review/Aggregate + FAQ + Breadcrumb
    const serviceSchema = {
        "@context": "https://schema.org",
        "@type": "Service",
        "serviceType": data.h1,
        "provider": {
            "@type": "EducationalOrganization",
            "@id": `${SITE_URL}/#organization`,
            "name": "Anand Tutorials",
            "url": SITE_URL,
            "logo": "https://anandtutorials.com/logo-anand-tutorials.png",
            "telephone": phone,
            "address": {
                "@type": "PostalAddress",
                "streetAddress": "Ghadi Chowk, Vijay Nagar",
                "addressLocality": "Jabalpur",
                "addressRegion": "Madhya Pradesh",
                "postalCode": "482002",
                "addressCountry": "IN",
            },
            "sameAs": [
                "https://www.facebook.com/anandtutorials",
                "https://www.instagram.com/anandtutorials",
            ],
        },
        "name": data.h1,
        "description": data.subTitle,
        "url": fullUrl,
        "areaServed": { "@type": "Country", "name": "India" },
        "audience": { "@type": "EducationalAudience", "educationalRole": "student" },
        "aggregateRating": {
            "@type": "AggregateRating",
            "ratingValue": RATING,
            "reviewCount": REVIEW_COUNT,
            "bestRating": "5",
            "worstRating": "1",
        },
        "review": NATIONAL_TESTIMONIALS.slice(0, 4).map((t) => ({
            "@type": "Review",
            "reviewRating": { "@type": "Rating", "ratingValue": String(t.rating), "bestRating": "5" },
            "author": { "@type": "Person", "name": t.name },
            "reviewBody": t.body,
        })),
    };

    const faqSchema = {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        "mainEntity": data.faqs.map(([q, a]) => ({
            "@type": "Question",
            "name": q,
            "acceptedAnswer": { "@type": "Answer", "text": a },
        })),
    };

    // Course schema — only emit when the page is genuinely an educational course
    // (subject/class/board/exam authority pages). Skip for comparison / pricing pages.
    const _courseEligible = data.intent && (
        data.intent.includes("tuition")
        || data.intent.includes("coaching")
        || data.intent.includes("class-")
    );
    const courseSchema = _courseEligible ? {
        "@context": "https://schema.org",
        "@type": "Course",
        "name": data.h1,
        "description": data.subTitle,
        "url": fullUrl,
        "provider": {
            "@type": "EducationalOrganization",
            "@id": `${SITE_URL}/#organization`,
            "name": "Anand Tutorials",
            "sameAs": SITE_URL,
        },
        "educationalCredentialAwarded": data.focusKeyword || data.h1,
        "audience": { "@type": "EducationalAudience", "educationalRole": "student" },
        "hasCourseInstance": [{
            "@type": "CourseInstance",
            "courseMode": "online",
            "courseWorkload": "PT4H",
            "inLanguage": "en",
            "name": data.h1,
            "offers": [{
                "@type": "Offer",
                "availability": "https://schema.org/InStock",
                "price": "0",
                "priceCurrency": "INR",
                "category": "Free demo class",
                "url": `${SITE_URL}/contact`,
            }],
        }],
        "aggregateRating": {
            "@type": "AggregateRating",
            "ratingValue": RATING,
            "reviewCount": REVIEW_COUNT,
            "bestRating": "5",
            "worstRating": "1",
        },
    } : null;

    return (
        <div data-testid={`authority-page-${slug}`} className="bg-white">
            <SEO
                title={data.seoTitle}
                description={data.seoDescription}
                keywords={data.keywords}
                path={data.path}
                hreflangs={slug === "online-classes" ? getInternationalHreflangs() : undefined}
                schemas={[
                    serviceSchema,
                    faqSchema,
                    courseSchema,
                    enrichedLocalBusiness,
                    slug === "online-classes" ? buildConversationalFAQSchema(ONLINE_CLASSES_AEO.faqs) : null,
                    slug === "foreign-students" ? buildConversationalFAQSchema(FOREIGN_STUDENTS_AEO.faqs) : null,
                    // Batch 2M — per-state AI-Overview FAQ schema
                    data.aeo ? buildConversationalFAQSchema(data.aeo.faqs) : null,
                ].filter(Boolean)}
            />

            {/* V6 hreflang alternates — fire on country×topic pages so Google
                serves the right country variant to each region's audience. */}
            {(() => {
                const currentCountry = _v6CountryFromPath(data.path);
                if (!currentCountry) return null;
                return (
                    <Helmet>
                        {HREFLANG_COUNTRIES.map(([cSlug, hl]) => {
                            const altPath = _v6AlternatePath(data.path, cSlug, currentCountry);
                            return altPath ? (
                                <link key={cSlug} rel="alternate" hrefLang={hl} href={`${SITE_URL}${altPath}`} />
                            ) : null;
                        })}
                        <link rel="alternate" hrefLang="en-IN" href={`${SITE_URL}/online-classes`} />
                        <link rel="alternate" hrefLang="x-default" href={`${SITE_URL}${data.path}`} />
                    </Helmet>
                );
            })()}

            <Breadcrumbs items={[{ name: data.h1 }]} />

            {/* Hero */}
            <PageHeader eyebrow={data.eyebrow} title={data.h1} subtitle={data.subTitle}>
                <div className="flex flex-wrap gap-3">
                    <Link to="/register" onClick={() => trackCTA("book_demo_clicked", { cta_location: "authority-hero" })} data-testid="authority-cta-demo" className="inline-flex items-center gap-2 bg-amber-500 text-[#0F172A] font-semibold px-5 py-3 rounded-lg hover:bg-amber-400 transition-colors">
                        Book Free Demo <ArrowRight className="w-4 h-4" />
                    </Link>
                    <a href={`tel:${phone.replace(/\s/g, "")}`} onClick={() => trackCTA("call_now_clicked", { cta_location: "authority-hero" })} data-testid="authority-cta-call" className="inline-flex items-center gap-2 bg-white/10 border border-white/20 backdrop-blur text-white font-semibold px-5 py-3 rounded-lg hover:bg-white/20 transition-colors">
                        <Phone className="w-4 h-4" /> Call Us
                    </a>
                    <a href={`https://wa.me/${whatsapp}?text=${encodeURIComponent(`Hi — interested in: ${data.h1}`)}`} onClick={() => trackCTA("whatsapp_clicked", { cta_location: "authority-hero" })} target="_blank" rel="noopener noreferrer" data-testid="authority-cta-whatsapp" className="inline-flex items-center gap-2 bg-[#22C55E] hover:bg-[#16A34A] text-white font-semibold px-5 py-3 rounded-lg transition-colors">
                        <MessageCircle className="w-4 h-4" /> WhatsApp
                    </a>
                </div>
            </PageHeader>

            {/* V65 — AEO Quick-Answer card (pan-India online-classes hub only) */}
            {slug === "online-classes" && (
                <QuickAnswerCard
                    question={ONLINE_CLASSES_AEO.quickAnswer.question}
                    answer={ONLINE_CLASSES_AEO.quickAnswer.answer}
                    bullets={ONLINE_CLASSES_AEO.quickAnswer.bullets}
                    testId="online-classes-quick-answer"
                />
            )}
            {/* V65 — AEO Quick-Answer card (foreign-students hub only) */}
            {slug === "foreign-students" && (
                <QuickAnswerCard
                    question={FOREIGN_STUDENTS_AEO.quickAnswer.question}
                    answer={FOREIGN_STUDENTS_AEO.quickAnswer.answer}
                    bullets={FOREIGN_STUDENTS_AEO.quickAnswer.bullets}
                    footer={FOREIGN_STUDENTS_AEO.quickAnswer.footer}
                    testId="foreign-students-quick-answer"
                />
            )}

            {/* Batch 2M (Feb-2026) — per-state / per-hub AEO Quick-Answer card */}
            {data.aeo && (
                <QuickAnswerCard
                    question={data.aeo.quickAnswer.question}
                    answer={data.aeo.quickAnswer.answer}
                    bullets={data.aeo.quickAnswer.bullets}
                    footer={data.aeo.quickAnswer.footer}
                    testId={`state-quick-answer-${data.path.replace(/[^a-z0-9]+/gi, "-").replace(/^-|-$/g, "")}`}
                />
            )}

            {/* Trust stats strip */}
            <section className="py-10 bg-[#F8FAFC] border-b border-slate-100">
                <div className="max-w-6xl mx-auto px-6 lg:px-8 grid grid-cols-2 md:grid-cols-4 gap-4">
                    {data.stats.map((s, i) => (
                        <div key={i} className="text-center">
                            <div className="font-heading font-bold text-2xl sm:text-3xl text-[#0F172A]">{s.v}</div>
                            <div className="text-xs uppercase tracking-wider text-slate-500 mt-1">{s.l}</div>
                        </div>
                    ))}
                </div>
            </section>

            {/* V7 — Near Me dynamic geo block (only on Near-Me pillar pages) */}
            {data.enableGeoBlock && (
                <section className="py-8 bg-white">
                    <div className="max-w-6xl mx-auto px-6 lg:px-8">
                        <NearMeGeoBlock />
                    </div>
                </section>
            )}

            {/* V7 — State page city interlink block (only on state pages) */}
            {Array.isArray(data.stateCities) && data.stateCities.length > 0 && (
                <section className="py-12 bg-white border-b border-slate-100">
                    <div className="max-w-6xl mx-auto px-6 lg:px-8">
                        <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-[#3B82F6] mb-3 flex items-center gap-2">
                            <MapPin className="w-3 h-3" /> Cities we serve in this state
                        </div>
                        <h2 className="font-heading text-2xl sm:text-3xl font-bold text-[#0F172A] mb-6">
                            1:1 online tuition active across every major city
                        </h2>
                        <div className="flex flex-wrap gap-2">
                            {data.stateCities.map((c) => {
                                const slug = c.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
                                return (
                                    <Link
                                        key={slug}
                                        to={`/online-tuition-${slug}`}
                                        data-testid={`state-city-link-${slug}`}
                                        className="text-sm px-3 py-2 rounded-lg bg-[#F8FAFC] border border-slate-200 hover:border-[#1E3A8A] hover:bg-white text-slate-700 hover:text-[#1E3A8A] font-semibold transition-colors inline-flex items-center gap-1.5"
                                    >
                                        {c} <ArrowRight className="w-3.5 h-3.5" />
                                    </Link>
                                );
                            })}
                        </div>
                    </div>
                </section>
            )}

            {/* V8 — City locality interlink block (only on priority-city keyword pages) */}
            {Array.isArray(data.cityLocalities) && data.cityLocalities.length > 0 && (
                <section className="py-12 bg-white border-b border-slate-100">
                    <div className="max-w-6xl mx-auto px-6 lg:px-8">
                        <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-[#3B82F6] mb-3 flex items-center gap-2">
                            <MapPin className="w-3 h-3" /> Localities we serve
                        </div>
                        <h2 className="font-heading text-2xl sm:text-3xl font-bold text-[#0F172A] mb-6">
                            Active 1:1 home-tutor deployment across your city
                        </h2>
                        <div className="flex flex-wrap gap-2">
                            {data.cityLocalities.map((loc) => (
                                <span
                                    key={loc.slug}
                                    data-testid={`locality-chip-${loc.slug}`}
                                    className="text-sm px-3 py-2 rounded-lg bg-[#F8FAFC] border border-slate-200 text-slate-700 font-semibold"
                                >
                                    {loc.name}
                                </span>
                            ))}
                        </div>
                    </div>
                </section>
            )}

            {/* Pillars — Why we are the best */}
            <section className="py-16 sm:py-20 bg-white">
                <div className="max-w-6xl mx-auto px-6 lg:px-8">
                    <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-[#3B82F6] mb-3 flex items-center gap-2">
                        <Sparkles className="w-3 h-3" /> What makes us the answer
                    </div>
                    <h2 className="font-heading text-3xl sm:text-4xl font-bold text-[#0F172A] mb-10 max-w-3xl">
                        Six reasons parents pick Anand Tutorials over every alternative.
                    </h2>
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
                        {data.pillars.map((p, i) => (
                            <div key={i} className="bg-[#F8FAFC] border border-slate-200 rounded-2xl p-6 hover:border-[#1E3A8A]/30 hover:shadow-md transition-all">
                                <div className="w-10 h-10 rounded-lg bg-[#1E3A8A] text-white flex items-center justify-center mb-4 font-heading font-bold">{i + 1}</div>
                                <h3 className="font-heading font-semibold text-[#0F172A] text-lg mb-2 leading-snug">{p.t}</h3>
                                <p className="text-sm text-slate-600 leading-relaxed">{p.d}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* EEAT block */}
            <section className="py-16 sm:py-20 bg-[#0F172A] text-white">
                <div className="max-w-6xl mx-auto px-6 lg:px-8">
                    <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-amber-300 mb-3 flex items-center gap-2">
                        <ShieldCheck className="w-3 h-3" /> Experience · Expertise · Authoritativeness · Trust
                    </div>
                    <h2 className="font-heading text-3xl sm:text-4xl font-bold mb-10 max-w-3xl">{data.eeatTitle}</h2>
                    <div className="grid md:grid-cols-2 gap-5">
                        {data.eeat.map((e, i) => (
                            <div key={i} className="bg-white/5 backdrop-blur border border-white/10 rounded-2xl p-6">
                                <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-amber-300 mb-2">{e.pillar}</div>
                                <p className="text-[15px] text-slate-200 leading-relaxed">{e.body}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Coverage */}
            <section className="py-16 sm:py-20 bg-white">
                <div className="max-w-6xl mx-auto px-6 lg:px-8">
                    <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-[#3B82F6] mb-3 flex items-center gap-2">
                        <GraduationCap className="w-3 h-3" /> Full topical coverage
                    </div>
                    <h2 className="font-heading text-3xl sm:text-4xl font-bold text-[#0F172A] mb-10 max-w-3xl">Topical authority across every Indian and international curriculum.</h2>
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                        {data.coverage.map((c, i) => (
                            <div key={i} className="bg-[#F8FAFC] border border-slate-200 rounded-xl p-5">
                                <div className="inline-flex items-center px-2 py-0.5 rounded-md bg-[#1E3A8A]/5 text-[10px] font-bold uppercase tracking-wider text-[#1E3A8A] mb-2">{c.tag}</div>
                                <p className="text-sm text-slate-700 leading-relaxed">{c.d}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Comparison table */}
            <section className="py-16 sm:py-20 bg-[#F8FAFC]">
                <div className="max-w-5xl mx-auto px-6 lg:px-8">
                    <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-amber-600 mb-3">Side by side</div>
                    <h2 className="font-heading text-3xl sm:text-4xl font-bold text-[#0F172A] mb-10 max-w-3xl">Anand Tutorials vs everyone else.</h2>
                    <div className="overflow-x-auto bg-white rounded-2xl border border-slate-200 shadow-sm">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="bg-[#0F172A] text-white">
                                    <th className="px-4 py-3 text-left font-heading font-semibold"></th>
                                    <th className="px-4 py-3 text-left font-heading font-semibold">Anand Tutorials</th>
                                    <th className="px-4 py-3 text-left font-heading font-semibold">Typical alternative</th>
                                </tr>
                            </thead>
                            <tbody>
                                {data.compare.map((row, i) => (
                                    <tr key={i} className={i % 2 ? "bg-[#F8FAFC]" : ""}>
                                        <td className="px-4 py-3 font-semibold text-[#0F172A]">{row[0]}</td>
                                        <td className="px-4 py-3 text-slate-700">
                                            <span className="inline-flex items-center gap-1.5">
                                                <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" /> {row[1]}
                                            </span>
                                        </td>
                                        <td className="px-4 py-3 text-slate-500">{row[2]}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </section>

            {/* Long-form content sections — deep SEO depth, target long-tail keywords */}
            {Array.isArray(data.longFormSections) && data.longFormSections.length > 0 && (
                <section className="py-16 sm:py-20 bg-[#F8FAFC]">
                    <div className="max-w-4xl mx-auto px-6 lg:px-8 space-y-10">
                        {data.longFormSections.map((s, i) => (
                            <article key={i} data-testid={`authority-longform-${i}`}>
                                <h2 className="font-heading text-2xl sm:text-3xl font-bold mb-4 text-[#0F172A]">{s.h2}</h2>
                                {s.paragraphs.map((p, j) => (
                                    <p key={j} className="text-slate-700 leading-relaxed text-base mb-4">{p}</p>
                                ))}
                                {s.bullets && (
                                    <ul className="mt-3 space-y-2 list-disc pl-6 text-slate-700">
                                        {s.bullets.map((b, k) => (
                                            <li key={k} className="text-[15px] leading-relaxed">{b}</li>
                                        ))}
                                    </ul>
                                )}
                            </article>
                        ))}
                    </div>
                </section>
            )}

            {/* Curriculum-pillar bidirectional link — deep-dive articles block.
                Rendered only when this pillar has a matching curriculum-guides
                sub-hub category and at least one published deep-dive article. */}
            {curriculumPillarMeta && curriculumArticles.length > 0 && (
                <section className="py-14 bg-white border-t border-slate-100" data-testid="curriculum-pillar-deepdives">
                    <div className="max-w-5xl mx-auto px-6 lg:px-8">
                        <div className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.22em] text-emerald-700 mb-3">
                            <BookOpen className="w-3 h-3" /> Curriculum deep-dives · {curriculumPillarMeta.region}
                        </div>
                        <div className="flex items-baseline justify-between flex-wrap gap-3 mb-2">
                            <h2 className="font-heading text-2xl sm:text-3xl font-bold text-[#0F172A]">
                                Editorial deep-dives on {curriculumPillarMeta.curriculum}
                            </h2>
                            <Link
                                to="/blog/category/curriculum-guides"
                                data-testid="curriculum-pillar-subhub-link"
                                className="text-sm font-semibold text-emerald-700 hover:text-emerald-800 inline-flex items-center gap-1"
                            >
                                View all curriculum deep-dives <ArrowRight className="w-3.5 h-3.5" />
                            </Link>
                        </div>
                        <p className="text-slate-600 mb-6 max-w-3xl">
                            Long-form articles written by mentors who teach {curriculumPillarMeta.curriculum} every week — sitting in the same editorial hub as GCSE UK, ACARA, VCE, HSC NSW and Leaving Cert Ireland.
                        </p>
                        <div className="grid md:grid-cols-2 gap-5">
                            {curriculumArticles.map((b) => (
                                <Link
                                    key={b.id}
                                    to={`/post/${b.slug}`}
                                    data-testid={`curriculum-pillar-deepdive-${b.slug}`}
                                    className="group bg-[#F8FAFC] rounded-2xl border border-slate-200 overflow-hidden hover:-translate-y-1 hover:shadow-lg hover:border-emerald-300 transition-all flex flex-col"
                                >
                                    {b.cover_image && (
                                        <div className="aspect-[16/9] overflow-hidden bg-slate-200">
                                            <img src={b.cover_image} alt={b.title} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                                        </div>
                                    )}
                                    <div className="p-5 flex flex-col flex-1">
                                        <div className="text-[10px] uppercase tracking-[0.15em] text-emerald-700 font-bold mb-2">Deep-dive · {curriculumPillarMeta.curriculum}</div>
                                        <h3 className="font-heading text-lg font-semibold text-[#0F172A] mb-2 group-hover:text-emerald-700 leading-snug">{b.title}</h3>
                                        <p className="text-sm text-slate-600 leading-relaxed line-clamp-3 flex-1">{b.excerpt}</p>
                                        <div className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-emerald-700 group-hover:gap-2.5 transition-all">
                                            Read the deep-dive <ArrowRight className="w-4 h-4" />
                                        </div>
                                    </div>
                                </Link>
                            ))}
                        </div>
                    </div>
                </section>
            )}

            {/* Testimonials */}
            <section className="py-16 sm:py-20 bg-white">
                <div className="max-w-6xl mx-auto px-6 lg:px-8">
                    <div className="text-center mb-10">
                        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-amber-50 border border-amber-200 text-[10px] uppercase tracking-[0.22em] font-bold text-amber-700 mb-4">
                            <Star className="w-3 h-3 fill-amber-500 text-amber-500" /> Verified parent reviews
                        </div>
                        <h2 className="font-heading text-3xl sm:text-4xl font-bold text-[#0F172A]">Trusted by parents across India.</h2>
                    </div>
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
                        {NATIONAL_TESTIMONIALS.map((t) => (
                            <div key={t.name} data-testid={`authority-testimonial-${t.name.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "")}`} className="relative bg-[#F8FAFC] border border-slate-200 rounded-2xl p-6">
                                <Quote className="absolute top-5 right-5 w-6 h-6 text-slate-200" />
                                <StarsRow n={t.rating} />
                                <p className="mt-4 text-[15px] text-slate-700 leading-relaxed">“{t.body}”</p>
                                <div className="mt-4 pt-4 border-t border-slate-100">
                                    <div className="font-heading font-semibold text-[#0F172A] text-sm">{t.name}</div>
                                    <div className="text-[11px] text-slate-500">{t.meta}</div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Find best home tutors near you — locality block */}
            <section className="py-16 bg-[#F8FAFC] border-y border-slate-100">
                <div className="max-w-6xl mx-auto px-6 lg:px-8">
                    <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-[#3B82F6] mb-3 flex items-center gap-2">
                        <MapPin className="w-3 h-3" /> Find best home tutors near you
                    </div>
                    <h2 className="font-heading text-3xl sm:text-4xl font-bold text-[#0F172A] mb-3 max-w-3xl">Hyperlocal verified tutors across India's most-searched localities.</h2>
                    <p className="text-slate-600 mb-8 max-w-3xl">Tap your area for a verified 1:1 home tutor. Don't see your locality? <a href={`tel:${phone.replace(/\s/g, "")}`} className="text-[#1E3A8A] font-semibold underline">Call us</a> — chances are we serve it.</p>
                    <div className="grid md:grid-cols-3 gap-6">
                        {Object.entries(NEAR_YOU_LOCALITIES).map(([city, areas]) => {
                            const citySlug = city.toLowerCase();
                            return (
                                <div key={city} className="bg-white border border-slate-200 rounded-2xl p-5">
                                    <Link to={`/${citySlug}`} className="font-heading font-semibold text-[#0F172A] hover:text-[#1E3A8A] mb-3 inline-flex items-center gap-1.5">
                                        Home tuition in {city} <ArrowRight className="w-3.5 h-3.5" />
                                    </Link>
                                    <div className="flex flex-wrap gap-2 mt-2">
                                        {areas.map((a) => (
                                            <Link
                                                key={a}
                                                to={`/cbse-home-tuition-${citySlug}?area=${encodeURIComponent(a)}`}
                                                data-testid={`authority-near-you-${citySlug}-${a.toLowerCase().replace(/\s+/g, "-")}`}
                                                className="text-xs px-2.5 py-1.5 rounded-full bg-slate-100 hover:bg-[#1E3A8A] hover:text-white text-slate-700 font-semibold transition-colors"
                                            >
                                                {a}
                                            </Link>
                                        ))}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* FAQs + Registration form */}
            <section className="py-16 sm:py-20 bg-white">
                <div className="max-w-7xl mx-auto px-6 lg:px-8 grid lg:grid-cols-5 gap-10 lg:gap-14">
                    <div className="lg:col-span-3">
                        <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-[#3B82F6] mb-3">Frequently asked</div>
                        <h2 className="font-heading text-3xl sm:text-4xl font-bold text-[#0F172A] mb-6">Parents ask — here's what we tell them.</h2>
                        <div className="space-y-3">
                            {data.faqs.map(([q, a], i) => (
                                <details key={q} data-testid={`authority-faq-${i}`} className="group bg-[#F8FAFC] border border-slate-200 rounded-xl p-5 [&_summary::-webkit-details-marker]:hidden">
                                    <summary className="cursor-pointer font-heading font-semibold text-[#0F172A] flex items-center justify-between gap-3">
                                        {q}
                                        <span className="text-[#1E3A8A] text-lg group-open:rotate-45 transition-transform">+</span>
                                    </summary>
                                    <div className="text-sm text-slate-600 mt-3 leading-relaxed">{a}</div>
                                </details>
                            ))}
                        </div>

                        {/* Strong CTA card */}
                        <div className="mt-10 p-6 rounded-2xl bg-gradient-to-br from-[#0F172A] to-[#1E3A8A] text-white">
                            <div className="text-[10px] font-bold uppercase tracking-wider text-amber-300 mb-2">Talk to us today</div>
                            <h3 className="font-heading text-xl font-bold mb-3">Book a free 1:1 demo. No card. No obligation.</h3>
                            <div className="flex flex-wrap gap-3">
                                <Link to="/register" onClick={() => trackCTA("book_demo_clicked", { cta_location: "authority-sticky-cta" })} className="inline-flex items-center gap-2 bg-amber-500 text-[#0F172A] font-semibold px-4 py-2.5 rounded-lg hover:bg-amber-400 transition-colors text-sm">
                                    Book Free Demo
                                </Link>
                                <a href={`tel:${phone.replace(/\s/g, "")}`} onClick={() => trackCTA("call_now_clicked", { cta_location: "authority-sticky-cta" })} className="inline-flex items-center gap-2 bg-white/10 border border-white/20 text-white font-semibold px-4 py-2.5 rounded-lg hover:bg-white/20 transition-colors text-sm">
                                    <Phone className="w-3.5 h-3.5" /> Call Now
                                </a>
                                <a href={`https://wa.me/${whatsapp}?text=${encodeURIComponent(`Hi — interested in: ${data.h1}`)}`} onClick={() => trackCTA("whatsapp_clicked", { cta_location: "authority-sticky-cta" })} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 bg-[#22C55E] hover:bg-[#16A34A] text-white font-semibold px-4 py-2.5 rounded-lg text-sm">
                                    <MessageCircle className="w-3.5 h-3.5" /> WhatsApp
                                </a>
                            </div>
                        </div>

                        {/* Popular courses in this city — Batch 2I Part 5 reverse cross-links */}
                        {data.relatedCourses && data.relatedCourses.length > 0 && (
                            <div className="mt-10 pt-8 border-t border-slate-100" data-testid={`authority-related-courses-${data.path.replace(/[^a-z0-9]+/gi, "-").replace(/^-|-$/g, "")}`}>
                                <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-emerald-600 mb-3 flex items-center gap-2">
                                    <GraduationCap className="w-3 h-3" /> Popular course tracks here
                                </div>
                                <h3 className="font-heading text-xl sm:text-2xl font-bold text-[#0F172A] mb-3">The 1:1 course pillars parents here ask for most.</h3>
                                <p className="text-sm text-slate-600 mb-6 max-w-3xl leading-relaxed">
                                    Each pillar below is our dedicated 1:1 mentor track — every tutor is board-code-matched, syllabus-fluent and rate-transparent. Tap any card to see the exam-pattern deep-dive, sample outcomes, mid-year plan and Q&amp;A.
                                </p>
                                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                                    {data.relatedCourses.map((rc, i) => (
                                        <Link
                                            key={rc.path}
                                            to={rc.path}
                                            data-testid={`authority-course-${data.path.replace(/[^a-z0-9]+/gi, "-").replace(/^-|-$/g, "")}-${i}`}
                                            className="group flex items-start gap-3 p-4 rounded-xl bg-gradient-to-br from-emerald-50 to-white border border-emerald-100 hover:border-emerald-500 hover:shadow-sm transition-all"
                                        >
                                            <div className="w-9 h-9 rounded-lg bg-emerald-600/10 text-emerald-700 flex items-center justify-center shrink-0 group-hover:bg-emerald-600 group-hover:text-white transition-colors">
                                                <GraduationCap className="w-5 h-5" />
                                            </div>
                                            <div className="min-w-0">
                                                <div className="font-heading font-semibold text-[#0F172A] text-sm mb-1 group-hover:text-emerald-700">
                                                    {rc.label}
                                                </div>
                                                <div className="text-xs text-slate-600 leading-relaxed">{rc.tagline}</div>
                                            </div>
                                        </Link>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Internal links — strong topical authority cluster */}
                        <div className="mt-10 pt-8 border-t border-slate-100">
                            <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-slate-400 mb-3 flex items-center gap-2">
                                <Award className="w-3 h-3" /> Related authority pages
                            </div>
                            <div className="flex flex-wrap gap-2">
                                {data.related.map((r) => (
                                    <Link
                                        key={r.to}
                                        to={r.to}
                                        data-testid={`authority-related-${r.to.replace(/[^a-z0-9]+/gi, "-").replace(/^-|-$/g, "")}`}
                                        className="text-xs px-3 py-1.5 rounded-full bg-slate-100 hover:bg-[#1E3A8A] hover:text-white text-slate-700 font-semibold transition-colors"
                                    >
                                        {r.label}
                                    </Link>
                                ))}
                            </div>
                        </div>
                    </div>

                    <div className="lg:col-span-2">
                        <div className="sticky top-24">
                            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-amber-50 border border-amber-200 text-[10px] uppercase tracking-[0.22em] font-bold text-amber-700 mb-3">
                                <GraduationCap className="w-3 h-3" /> Free demo class
                            </div>
                            <h3 className="font-heading text-xl font-bold text-[#0F172A] mb-1">Book your demo</h3>
                            <p className="text-xs text-slate-500 mb-4">
                                <Star className="w-3 h-3 inline-block mr-1 fill-amber-400 text-amber-400" />
                                {RATING}/5 across {REVIEW_COUNT}+ verified parent reviews
                            </p>
                            <RegistrationForm />
                        </div>
                    </div>
                </div>
            </section>

            {/* V65 — AEO Conversational FAQ (pan-India online-classes hub only) */}
            {slug === "online-classes" && (
                <ConversationalFAQ
                    heading="Deeper questions about 1:1 online tuition across India"
                    subheading="Long-form answers for parents comparing 1:1 online tuition to traditional coaching or recorded courses."
                    items={ONLINE_CLASSES_AEO.faqs}
                    testId="online-classes-conversational-faq"
                />
            )}
            {/* V65 — AEO Definition + Conversational FAQ (foreign-students hub only) */}
            {slug === "foreign-students" && (
                <>
                    <DefinitionBox
                        term={FOREIGN_STUDENTS_AEO.definition.term}
                        definition={FOREIGN_STUDENTS_AEO.definition.definition}
                        examples={FOREIGN_STUDENTS_AEO.definition.examples}
                        testId="foreign-students-definition"
                    />
                    <ConversationalFAQ
                        heading="Deeper questions from NRI + international families"
                        subheading="Long-form answers to the questions we most often hear on enquiry calls from families outside India."
                        items={FOREIGN_STUDENTS_AEO.faqs}
                        testId="foreign-students-conversational-faq"
                    />
                </>
            )}

            {/* Batch 2M (Feb-2026) — per-state / per-hub AEO Conversational FAQ */}
            {data.aeo && (
                <ConversationalFAQ
                    heading={`Common questions parents in ${data.aeo.quickAnswer.question.replace(/^.*for /, "").replace(/[?!]?\s*$/, "").replace(/ students$/, "")} ask us`}
                    subheading="Direct answers to the two questions Google's AI Overviews most often need — matched to your board, currency and school ecosystem."
                    items={data.aeo.faqs}
                    testId={`state-conversational-faq-${data.path.replace(/[^a-z0-9]+/gi, "-").replace(/^-|-$/g, "")}`}
                />
            )}
        </div>
    );
}
